import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import laboratoriesStrings from 'modules/labs/laboratories/strings';
import VendorsContainer from 'modules/shared/vendors/containers/VendorsContainer';
import { fetchAllLabsWithFilters, fetchAddLabSubmission } from 'modules/labs/laboratories/actions';
import {
  labsStatusEnum,
  labsStatusColorEnum,
  filters,
  addLabFormValidationErrorType,
} from 'modules/labs/laboratories/enums';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const requiredFormFields = [
  'englishDisplayName',
  'englishName',
  'englishAddress',
  'arabicName',
  'arabicDisplayName',
  'arabicAddress',
  'lat',
  'long',
  'contactPerson',
  'email',
  'phone',
  'deliveryRadius',
  'partnershipDeliveryRadius',
  'deliveryCharge',
  'isActive',
];
const optionalFormFieldsWithRegexValidation = [];

const requiredFormFieldsWithRegexValidation = {
  email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i,
  phone: /^01[0-9]{9}$/i,
  lat: /^-?([1-8]?[1-9]|[1-9]0)\.{1}\d{1,6}/i,
  long: /^-?([1-8]?[1-9]|[1-9]0)\.{1}\d{1,6}/i,
};

// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################

const LabsView = props => {
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const { allLabsQueryStrings } = props;
  const [labsQueryStrings, setLabsQueryStrings] = useState(allLabsQueryStrings);
  const [selectedPageFilter, setSelectedPageFilter] = useState(1);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [formFieldsValues, setFormFieldsValues] = useState({
    englishName: '',
    englishDisplayName: '',
    englishAddress: '',
    arabicName: '',
    arabicDisplayName: '',
    arabicAddress: '',
    lat: '',
    long: '',
    contactPerson: '',
    email: '',
    phone: '',
    deliveryRadius: '',
    partnershipDeliveryRadius: '',
    deliveryCharge: '',
    adminNote: '',
    isActive: true,
    Key: '',
  });

  const [formFieldsValidation, setFormFieldsValidation] = useState({
    englishName: addLabFormValidationErrorType.valid,
    englishDisplayName: addLabFormValidationErrorType.valid,
    englishAddress: addLabFormValidationErrorType.valid,
    arabicName: addLabFormValidationErrorType.valid,
    arabicDisplayName: addLabFormValidationErrorType.valid,
    arabicAddress: addLabFormValidationErrorType.valid,
    lat: addLabFormValidationErrorType.valid,
    long: addLabFormValidationErrorType.valid,
    contactPerson: addLabFormValidationErrorType.valid,
    email: addLabFormValidationErrorType.valid,
    phone: addLabFormValidationErrorType.valid,
    deliveryRadius: addLabFormValidationErrorType.valid,
    partnershipDeliveryRadius: addLabFormValidationErrorType.valid,
    deliveryCharge: addLabFormValidationErrorType.valid,
    adminNote: addLabFormValidationErrorType.valid,
    isActive: addLabFormValidationErrorType.valid,
  });
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const dispatch = useDispatch();

  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const labsList = useSelector(state => state.laboratories.laboratoriesList);
  const totalResultsCount = labsList?.details?.totalCount;
  const numberOfPages =
    (totalResultsCount && Math.ceil(totalResultsCount / allLabsQueryStrings.PageSize)) || 1;
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleGenericFilterClick = (selectedSearchTerm, selectedSearchValue, filtersToClear) => {
    setSelectedPageFilter(1);
    const body = {
      ...labsQueryStrings,
    };

    if (selectedSearchTerm === undefined) {
      Object.keys(selectedSearchValue).forEach(key => {
        if (selectedSearchValue[key] === false) delete body[key];
        else body[key] = true;
      });
    } else if (Object.keys(selectedSearchValue).length > 0) {
      body[selectedSearchTerm] = Object.keys(selectedSearchValue);
    } else {
      delete body[selectedSearchTerm];
      filtersToClear?.forEach(filterKey => delete body[filterKey]);
    }

    setLabsQueryStrings(body);
    dispatch(fetchAllLabsWithFilters(body));
  };

  const handleLabsSearchBarInputChange = (searchQuery, reset) => {
    setSelectedPageFilter(1);

    const queryString = {
      ...labsQueryStrings,
    };
    if (searchQuery) queryString.locationName = searchQuery;
    if (reset) delete queryString.locationName;

    setLabsQueryStrings(queryString);

    dispatch(fetchAllLabsWithFilters(queryString));
  };

  const handleLabsPaginationClick = selectedPageNumber => {
    setSelectedPageFilter(selectedPageNumber);

    const queryString = {
      ...labsQueryStrings,
    };
    queryString.pageNumber = selectedPageNumber;

    dispatch(fetchAllLabsWithFilters(queryString));
  };

  const addLabSuccessCallback = () => {
    dispatch(fetchAllLabsWithFilters(allLabsQueryStrings));
  };

  const handleConfirmDetailsModalClick = functionCallBack => {
    let isFormValid = true;

    // we get all required fields and then make sure it has a value
    // and if it has a special regex requirement we validate it against that regex
    requiredFormFields.forEach(fieldName => {
      let isFieldValid = addLabFormValidationErrorType.valid;

      if (
        !formFieldsValues[fieldName].toString() ||
        (requiredFormFieldsWithRegexValidation[fieldName] &&
          requiredFormFieldsWithRegexValidation[fieldName].test(formFieldsValues[fieldName]) ===
            false)
      ) {
        isFieldValid =
          (!formFieldsValues[fieldName] && addLabFormValidationErrorType.empty) ||
          addLabFormValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: isFieldValid,
      }));
    });

    // we get all optional fields that require specific regix if value is entered
    // and we validate it against that regex
    Object.keys(optionalFormFieldsWithRegexValidation).forEach(key => {
      const fieldValue = formFieldsValues[key];

      let isFieldValid = addLabFormValidationErrorType.valid;
      if (fieldValue && optionalFormFieldsWithRegexValidation[key].test(fieldValue) === false) {
        isFieldValid = addLabFormValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [key]: isFieldValid,
      }));
    });

    if (isFormValid === false) return;

    const actionBody = {
      addLabPayload: {
        translators: [
          {
            address: formFieldsValues.arabicAddress,
            name: formFieldsValues.arabicName,
            displayName: formFieldsValues.arabicDisplayName,
            language: 'ar',
          },
          {
            address: formFieldsValues.englishAddress,
            name: formFieldsValues.englishName,
            displayName: formFieldsValues.englishDisplayName,
            language: 'en',
          },
        ],
        email: formFieldsValues.email,
        managerName: formFieldsValues.contactPerson,
        contactNumber: formFieldsValues.phone,
        note: formFieldsValues.adminNote,
        isActive: formFieldsValues.isActive,
        longitude: formFieldsValues.long,
        latitude: formFieldsValues.lat,
        deliveryFee: formFieldsValues.deliveryCharge,
        maxDeliveryDistancePS: formFieldsValues.partnershipDeliveryRadius,
        maxDeliveryDistance: formFieldsValues.deliveryRadius,
        pharmacyTypeId: 1,
        key: formFieldsValues.key,
        VezeetaProductKey: 'Labs',
      },
      callbackAction: addLabSuccessCallback,
    };
    dispatch(fetchAddLabSubmission(actionBody));
    setFormFieldsValues({
      englishName: '',
      englishDisplayName: '',
      englishAddress: '',
      arabicName: '',
      arabicDisplayName: '',
      arabicAddress: '',
      lat: '',
      long: '',
      contactPerson: '',
      email: '',
      phone: '',
      deliveryRadius: '',
      partnershipDeliveryRadius: '',
      deliveryCharge: '',
      adminNote: '',
      isActive: true,
    });
    functionCallBack(false);
    // dispatch(fetchAllLabsWithFilters(allLabsQueryStrings));
  };

  const handleCancelDetailsModalClick = functionCallBack => {
    setFormFieldsValidation({
      englishName: addLabFormValidationErrorType.valid,
      englishDisplayName: addLabFormValidationErrorType.valid,
      englishAddress: addLabFormValidationErrorType.valid,
      arabicName: addLabFormValidationErrorType.valid,
      arabicDisplayName: addLabFormValidationErrorType.valid,
      arabicAddress: addLabFormValidationErrorType.valid,
      lat: addLabFormValidationErrorType.valid,
      long: addLabFormValidationErrorType.valid,
      contactPerson: addLabFormValidationErrorType.valid,
      email: addLabFormValidationErrorType.valid,
      phone: addLabFormValidationErrorType.valid,
      deliveryRadius: addLabFormValidationErrorType.valid,
      partnershipDeliveryRadius: addLabFormValidationErrorType.valid,
      deliveryCharge: addLabFormValidationErrorType.valid,
      adminNote: addLabFormValidationErrorType.valid,
      isActive: addLabFormValidationErrorType.valid,
    });
    setFormFieldsValues({
      englishName: '',
      englishDisplayName: '',
      englishAddress: '',
      arabicName: '',
      arabicDisplayName: '',
      arabicAddress: '',
      lat: '',
      long: '',
      contactPerson: '',
      email: '',
      phone: '',
      deliveryRadius: '',
      partnershipDeliveryRadius: '',
      deliveryCharge: '',
      adminNote: '',
      isActive: true,
    });
    functionCallBack(false);
  };

  const handleVendorEditButtonClick = vendorDetails => {
    const vendorEnglishDetails = vendorDetails.translators.find(
      details => details.language === 'en',
    );
    const vendorArabicDetails = vendorDetails.translators.find(
      details => details.language === 'ar',
    );

    setFormFieldsValues({
      englishName: vendorEnglishDetails.name,
      englishDisplayName: vendorEnglishDetails.displayName,
      englishAddress: vendorEnglishDetails.address,
      arabicName: vendorArabicDetails.name,
      arabicDisplayName: vendorArabicDetails.displayName,
      arabicAddress: vendorArabicDetails.address,
      lat: vendorDetails.latitude,
      long: vendorDetails.longitude,
      contactPerson: vendorDetails.managerName,
      email: vendorDetails.email,
      phone: vendorDetails.contactNumber,
      deliveryRadius: vendorDetails.maxDeliveryDistance,
      partnershipDeliveryRadius: vendorDetails.maxDeliveryDistancePS,
      deliveryCharge: vendorDetails.deliveryFee,
      adminNote: vendorDetails.note,
      isActive: vendorDetails.isActive,
      key: vendorDetails.key,
    });
    setIsDetailsModalOpen(true);
  };

  const handleDropDownFieldChange = selectedObj => {
    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj.pharmacyTypeId = selectedObj.key;
      return fieldsObj;
    });
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################

  return (
    <VendorsContainer
      stringsData={laboratoriesStrings}
      selectedPageFilter={selectedPageFilter}
      numberOfPages={numberOfPages}
      vendorsList={labsList}
      mappedVendorsList={labsList?.details?.results || []}
      vendorStatusEnum={labsStatusEnum}
      vendorStatusColorEnum={labsStatusColorEnum}
      handleVendorsGenericFilterChange={handleGenericFilterClick}
      handleVendorsSearchBarInputChange={handleLabsSearchBarInputChange}
      handleVendorsPaginationClick={handleLabsPaginationClick}
      filters={filters}
      labsQueryStrings={labsQueryStrings}
      formFieldsValues={formFieldsValues}
      setFormFieldsValues={setFormFieldsValues}
      formFieldsValidation={formFieldsValidation}
      isDetailsModalOpen={isDetailsModalOpen}
      setIsDetailsModalOpen={setIsDetailsModalOpen}
      handleConfirmDetailsModalClick={handleConfirmDetailsModalClick}
      handleCancelDetailsModalClick={handleCancelDetailsModalClick}
      handleVendorEditButtonClick={handleVendorEditButtonClick}
      handleDropDownFieldChange={handleDropDownFieldChange}
      {...props}
    />
  );

  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

LabsView.propTypes = {
  allLabsQueryStrings: PropTypes.shape({
    PageSize: PropTypes.number,
  }).isRequired,
};

export default LabsView;
